import { Dialog } from "@jugl-web/ui-components";
import { Button, Checkbox } from "@jugl-web/ui-kit";
import { useTranslations } from "@jugl-web/utils";
import { FC, useCallback, useEffect, useState } from "react";
import { ReactComponent as ArrowRightIcon } from "./assets/arrow-right.svg";

export interface ResolveTicketDialogProps {
  isOpen: boolean;
  ticketUniqueId: string;
  onClose: () => void;
  onConfirm: (options: { skipFutureAlerts: boolean }) => void;
}

export const ResolveTicketDialog: FC<ResolveTicketDialogProps> = ({
  isOpen,
  ticketUniqueId,
  onClose,
  onConfirm,
}) => {
  const [skipFutureAlerts, setSkipFutureAlerts] = useState(false);

  const { t } = useTranslations();

  const handleConfirm = useCallback(() => {
    onConfirm({ skipFutureAlerts });
    onClose();
  }, [onClose, onConfirm, skipFutureAlerts]);

  useEffect(() => {
    if (isOpen) {
      setSkipFutureAlerts(false);
    }
  }, [isOpen]);

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      className="flex w-[480px] flex-col overflow-hidden rounded-2xl p-10"
    >
      <span className="font-secondary text-[22px] font-bold text-dark">
        {t({
          id: "client-support-page.resolve-ticket-emoji",
          defaultMessage: "🎟️ Resolve Ticket",
        })}
      </span>
      <span className="mt-2.5 font-secondary text-sm leading-[21px] text-dark-700">
        {t(
          {
            id: "client-support-page.resolve-ticket-description",
            defaultMessage:
              "Are you sure you want to resolve {ticketId} ticket? It can be reopened any time",
          },
          {
            ticketId: (
              <span className="bg-linear-to-r from-[#2196F3] to-[#8A41D3] bg-clip-text font-medium text-transparent">
                #{ticketUniqueId}
              </span>
            ),
          }
        )}
      </span>
      <div className="mt-[18px]">
        <Checkbox
          label={t({
            id: "common.dont-ask-me-again",
            defaultMessage: "Don't ask me again",
          })}
          labelClassName="font-secondary text-[13px] text-dark-900 leading-[150%]"
          isChecked={skipFutureAlerts}
          onChange={(event) => setSkipFutureAlerts(event.target.checked)}
        />
      </div>
      <div className="mt-14 flex items-center gap-4">
        <Button onClick={onClose} variant="tertiary" className="w-[140px]">
          {t({
            id: "common.cancel",
            defaultMessage: "Cancel",
          })}
        </Button>
        <Button
          onClick={handleConfirm}
          iconEnd={<ArrowRightIcon />}
          className="flex-1"
        >
          {t({
            id: "client-support-page.resolve",
            defaultMessage: "Resolve",
          })}
        </Button>
      </div>
    </Dialog>
  );
};
